import {
  AppSubscriptionStatus,
  AppSubscriptionsFragment
} from "@/gql/__generated__/graphql";
import { isBefore } from "date-fns";
import { useEffect, useState } from "react";
import {
  SubscriptionOrganizationType,
  SubscriptionTable,
  isInboundSubscription,
  isOutboundSubscription
} from "../types/subscriptions.type";

export function useInboundSubscriptionTableData(
  subscriptions: AppSubscriptionsFragment[],
  filterInactive: boolean
) {
  return useSubscriptionTableData(
    subscriptions,
    filterInactive,
    "outboundOrganization"
  );
}

export function useOutboundSubscriptionTableData(
  subscriptions: AppSubscriptionsFragment[],
  filterInactive: boolean
) {
  return useSubscriptionTableData(
    subscriptions,
    filterInactive,
    "inboundOrganization"
  );
}

function useSubscriptionTableData(
  subscriptions: AppSubscriptionsFragment[],
  filterInactive: boolean,
  type: SubscriptionOrganizationType
) {
  const [subscriptionState, setSubscriptionState] = useState<
    SubscriptionTable[]
  >([]);

  useEffect(() => {
    if (subscriptions.length < 1) return;
    const currentDate = new Date();
    const tempSubscriptions: SubscriptionTable[] = subscriptions
      .filter(
        (sub) =>
          filterInactive ||
          sub.status === AppSubscriptionStatus.Active ||
          (sub.expiresAt && isBefore(currentDate, sub.expiresAt))
      )
      .map((sub) => convertSubscriptionToTableData(sub, type));

    setSubscriptionState(tempSubscriptions);
  }, [filterInactive, subscriptions, type]);

  return subscriptionState;
}

function convertSubscriptionToTableData(
  subscription: AppSubscriptionsFragment,
  organizationType: SubscriptionOrganizationType
): SubscriptionTable {
  const type =
    organizationType === "inboundOrganization" ? "outbound" : "inbound";
  const reference = type === "outbound" ? subscription.name : undefined;
  const renewsAt =
    subscription.status === AppSubscriptionStatus.Active
      ? subscription.expiresAt
      : undefined;
  const expiresAt =
    subscription.status !== AppSubscriptionStatus.Active
      ? subscription.expiresAt
      : undefined;

  const tableData = {
    id: subscription.id,
    appName: subscription.app.displayName,
    appImage: subscription.app.pictureUrl,
    organizationName: subscription[organizationType].name,
    status: subscription.status,
    createdAt: subscription.createdAt,
    monthlyPriceUSD: subscription.monthlyPriceUSD,
    renewsAt,
    expiresAt,
    type,
    reference: reference || ""
  } as SubscriptionTable;

  if (isOutboundSubscription(tableData) || isInboundSubscription(tableData)) {
    return tableData;
  }
  throw new Error("Invalid organization type");
}
